import { useCallback, useMemo, useState } from "react";
import {
  ALL_ELEMENTS_GROUP_LABEL,
  EMPTY_ARRAY,
  EMPTY_BY_GROUP,
} from "../useGroupSelectionV3/constants";
import { getFirstElementAcrossGroups } from "../../utils/getFirstElementAcrossGroups";
import { scopeElementsToRooms } from "../../utils/rooms";
import type { ResetToPlanStart, UseGroupSelectionStateArgs } from "./types";

export const useGroupSelectionState = ({
  selectedPlan,
  rooms,
}: UseGroupSelectionStateArgs) => {
  const [selectedGroupLabel, setSelectedGroupLabel] = useState<string | null>(
    () => ALL_ELEMENTS_GROUP_LABEL,
  );

  const [selectedElementId, setSelectedElementId] = useState<string | null>(
    () => getFirstElementAcrossGroups(selectedPlan)?.id ?? null,
  );

  const planGroupIds = selectedPlan?.groupedElements?.groupIds ?? EMPTY_ARRAY;

  const planByGroupId = useMemo(
    () => selectedPlan?.groupedElements?.byGroupId ?? EMPTY_BY_GROUP,
    [selectedPlan?.groupedElements?.byGroupId],
  );

  // With no rooms this returns the plan data untouched, so planning mode is unaffected.
  const { groupIds, byGroupId } = useMemo(
    () =>
      scopeElementsToRooms({
        groupIds: planGroupIds,
        byGroupId: planByGroupId,
        rooms,
      }),
    [planGroupIds, planByGroupId, rooms],
  );

  const allPlanElements = useMemo(
    () => groupIds.flatMap((groupId) => byGroupId[groupId] ?? []),
    [groupIds, byGroupId],
  );

  const isAllElementsMode = selectedGroupLabel === ALL_ELEMENTS_GROUP_LABEL;

  const selectedGroupElements = useMemo(
    () =>
      isAllElementsMode
        ? allPlanElements
        : selectedGroupLabel
          ? (byGroupId[selectedGroupLabel] ?? [])
          : [],
    [isAllElementsMode, allPlanElements, byGroupId, selectedGroupLabel],
  );

  const selectedElement = useMemo(() => {
    if (!selectedElementId) return null;
    return (
      selectedGroupElements.find(
        (element) => element.id === selectedElementId,
      ) ?? null
    );
  }, [selectedGroupElements, selectedElementId]);

  const resetToPlanStart: ResetToPlanStart = useCallback((plan) => {
    setSelectedGroupLabel(ALL_ELEMENTS_GROUP_LABEL);
    const firstElement = getFirstElementAcrossGroups(plan);
    setSelectedElementId(firstElement?.id ?? null);
    return { groupId: ALL_ELEMENTS_GROUP_LABEL, element: firstElement };
  }, []);

  return {
    selectedGroupLabel,
    setSelectedGroupLabel,
    selectedElementId,
    setSelectedElementId,
    groupIds,
    byGroupId,
    allPlanElements,
    selectedGroupElements,
    selectedElement,
    resetToPlanStart,
  };
};
